// @flow
import React from 'react';
import {connect} from 'react-redux';

import {keepUpdatedEnhancer} from 'app/general/keepUpdatedEnhancer';
import {
  runWhenAnyModelMatch,
  type ModelSubscribedComponentPropsType,
  type ModelChangeMessageType,
} from 'app/subscriptions';
import {withLoadingIndicator} from 'app/general/sweet-loading-bar';
import {gt, ngt} from 'app/utils/i18n';

import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {ResetFilterMessage} from 'app/general/dashboards/ResetFilterMessage';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {DashboardSidebarFoldersList} from 'app/components/general/DashboardPage/DashboardSidebarFoldersList';
import MainCol from 'app/components/general/MainCol';
import {BootstrapMinMediumMediaQuery} from 'app/general/mediaqueries';
import {StyledSidebarDIV} from 'app/components/general/Sidebar';
import {
  fetchProcessesForDashboard,
  convertLocationParamsToFetchParams,
  toggleProcessDashboardRelation,
} from 'app/process/actions';
import {PROCESS_DASHBOARD_URL, PROCESS_CREATE_PAGE_URL} from 'app/urls';
import {SPPage} from 'app/general/structures/SPPage';
import {type contentType, getContentType} from 'app/utils/content-type';
import type {
  ProcessType,
  ProcessDashboardLocationParamsType,
} from 'app/process/types';
import {CompactProcessListItem} from 'app/process/components/CompactProcessListItem';
import {LinkPagination} from 'app/components/general/LinkPagination';
import {type StoredResponseType} from 'app/utils/redux_v2';
import {Filterbar} from 'app/components/general/DashboardPage/Filterbar';
import {shallowEqual} from 'app/utils/react-utils';
import {DocumentsNotificationsProvider} from 'app/notifly/components/DocumentsNotificationsProvider';
import {CreateDocumentButton} from 'app/components/general/CreateDocumentButton';
import {denormalizeProcessesList} from 'app/process/datafetchers';
import {type WithRouterProps} from 'app/general/withTypedRouter';
import type {StoreShapeType} from 'app/reducers/types';
import {PresenceSubscriber} from 'app/presence/components/PresenceSubscriber';
import {FolderLink} from 'app/folders/components/FolderLink';
import {DashboardItemsCounter} from 'app/general/dashboards/DashboardItemsCounter';

const processCt = getContentType('proceduregroup');

export const fetchParamsAreDifferent = (
  params: ProcessDashboardLocationParamsType,
  nextParams: ProcessDashboardLocationParamsType
) => {
  const fetchParams = convertLocationParamsToFetchParams(params);
  const nextFetchParams = convertLocationParamsToFetchParams(nextParams);
  return !shallowEqual(fetchParams, nextFetchParams);
};

type RouterProps = WithRouterProps<ProcessDashboardLocationParamsType>;

type OwnProps = {||};

type RoutedOwnProps = {|
  ...OwnProps,
  ...RouterProps,
|};

type ModelSubscribedProps = {|
  ...RoutedOwnProps,
  ...ModelSubscribedComponentPropsType,
|};

type StateProps = {|
  processes: StoredResponseType<ProcessType>,
  storedFilters: ProcessDashboardLocationParamsType,
  isFetching: boolean,
  isInvalid: boolean,
  activeRelations: {[contentType]: boolean},
|};

type DispatchProps = {|
  withLoadingIndicator: AsyncAction<typeof withLoadingIndicator, Promise<any>>,
  fetchProcessesForDashboard: AsyncAction<
    typeof fetchProcessesForDashboard,
    Promise<any>
  >,
  toggleProcessDashboardRelation: typeof toggleProcessDashboardRelation,
|};

type Props = {
  ...ModelSubscribedProps,
  ...StateProps,
  ...DispatchProps,
};

class BaseProcessesDashboard extends React.PureComponent<Props> {
  constructor(props: Props) {
    super(props);
    this.props.onModelChange(this.handleModelChange);
  }

  UNSAFE_componentWillMount() {
    const {location, storedFilters, router} = this.props;

    if (
      !fetchParamsAreDifferent(location.query, {}) &&
      fetchParamsAreDifferent(location.query, storedFilters)
    ) {
      router.replace({
        pathname: location.pathname,
        query: storedFilters,
      });
    } else {
      this.refreshData();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {query} = this.props.location;

    if (
      fetchParamsAreDifferent(query, nextProps.location.query) ||
      (!nextProps.isFetching && nextProps.isInvalid)
    ) {
      this.fetchProcesses(nextProps.location.query);
    }
  }

  handleModelChange = (message: ModelChangeMessageType) => {
    runWhenAnyModelMatch(message, ['process'], this.refreshData);
  };

  refreshData = () => this.fetchProcesses(this.props.location.query);

  fetchProcesses(query: ProcessDashboardLocationParamsType) {
    const {withLoadingIndicator, fetchProcessesForDashboard} = this.props;

    return withLoadingIndicator(() => fetchProcessesForDashboard(query));
  }

  resetFilters = () => {
    const {router, location} = this.props;
    router.replace({pathname: location.pathname});
  };

  render() {
    const {
      processes,
      location,
      activeRelations,
      toggleProcessDashboardRelation,
    } = this.props;
    const {query} = this.props.location;
    const viewingAll = !fetchParamsAreDifferent({}, query);

    return (
      <SPPage
        actionArea={({isTeamMember}) => (
          <CreateDocumentButton
            contentType="proceduregroup"
            disabled={!isTeamMember}
          />
        )}
        pageTitle={gt('Processes')}
      >
        {({isTeamMember}) => (
          <div className="row">
            <BootstrapMinMediumMediaQuery>
              <StyledSidebarDIV>
                <DashboardSidebarFoldersList />
              </StyledSidebarDIV>
            </BootstrapMinMediumMediaQuery>
            <MainCol>
              <Filterbar
                ct="proceduregroup"
                resetFilters={this.resetFilters}
                viewingAll={viewingAll}
                hideFilters={['private', 'public', 'is_in_knowledgebase', 'is_not_in_knowledgebase']}
                show={{
                  team: true,
                  tags: true,
                  sort: true,
                  filters: true,
                  search: true,
                }}
              />

              {!processes.loading ? (
                <DashboardItemsCounter
                  text={ngt('%s process', '%s processes', processes.count)}
                  showSearchHelp={query.search != null}
                />
              ) : null}

              {(processes.loading === true || processes.count > 0) &&
              query.folder != null ? (
                <div className="my-2 text-center">
                  <h4>
                    <i className="far fa-folder-open" /> {gt('Folder contents')}
                  </h4>
                  <span>
                    <button
                      className="btn-outline-secondary btn btn-sm"
                      onClick={this.resetFilters}
                    >
                      <i className="fa fa-times" />{' '}
                      {gt('Reset filters and view all processes.')}
                    </button>
                    <FolderLink hashid={query.folder} className="small ml-3" />
                  </span>
                </div>
              ) : null}

              <DocumentsNotificationsProvider
                contentType="proceduregroup"
                documentsIds={processes.results.map((p) => p.id)}
              >
                {(processesNotifications) =>
                  processes.results.map((process) => (
                    <CompactProcessListItem
                      key={process.id}
                      object={process}
                      sortBy={query.ordering}
                      activeRelations={activeRelations}
                      toggleRelation={toggleProcessDashboardRelation}
                      refreshData={this.refreshData}
                      notifications={processesNotifications[process.url]}
                    />
                  ))
                }
              </DocumentsNotificationsProvider>
              <PresenceSubscriber
                contentType="proceduregroup"
                objectsIds={processes.results.map((p) => p.id)}
                stealth={true}
              />

              {processes.loading === false && processes.count === 0 ? (
                <NoResultsMessage
                  className="js-procedures-list js-edit-dropdown"
                  head={
                    viewingAll ? (
                      gt('No processes on your account')
                    ) : query.folder != null ? (
                      <h4>
                        <i className="far fa-folder-open" />{' '}
                        {gt('Folder contents')}
                      </h4>
                    ) : (
                      gt('No processes found with these filters')
                    )
                  }
                >
                  {(FullHTMLLink) =>
                    viewingAll ? (
                      isTeamMember ? (
                        <FullHTMLLink
                          className="btn btn-primary btn-lg"
                          html_url={PROCESS_CREATE_PAGE_URL}
                        >
                          {gt('Create a Process')}
                        </FullHTMLLink>
                      ) : null
                    ) : (
                      <span>
                        <ResetFilterMessage
                          query={query}
                          resetFilters={this.resetFilters}
                          ct={'proceduregroup'}
                        />
                        {query.folder != null ? (
                          <FolderLink
                            hashid={query.folder}
                            className="small ml-3"
                          />
                        ) : null}
                      </span>
                    )
                  }
                </NoResultsMessage>
              ) : null}

              {!processes.loading ? (
                <LinkPagination
                  page={location.query.page}
                  response={processes}
                />
              ) : null}
            </MainCol>
          </div>
        )}
      </SPPage>
    );
  }
}

const mapStateToProps = (state, ownProps): StateProps => ({
  // $FlowFixMe
  isFetching: state.processArea.get('isFetching'),
  // $FlowFixMe
  isInvalid: state.processArea.get('isInvalid'),
  processes: denormalizeProcessesList(
    state,
    convertLocationParamsToFetchParams(ownProps.location.query)
  ),
  storedFilters: state.processArea
    .getIn(
      ['dashboard', 'currentFilters']
      // $FlowFixMe
    )
    .toJS(),
  activeRelations: state.processArea
    .getIn(
      ['dashboard', 'activeRelations']
      // $FlowFixMe
    )
    .toJS(),
});

const mapDispatchToProps: DispatchProps = {
  // $FlowFixMe
  withLoadingIndicator,
  // $FlowFixMe
  fetchProcessesForDashboard,
  // $FlowFixMe
  toggleProcessDashboardRelation,
};

export const ProcessesDashboard = keepUpdatedEnhancer<RoutedOwnProps>(
  connect<
    Props,
    ModelSubscribedProps,
    StateProps,
    DispatchProps,
    StoreShapeType,
    _
  >(
    mapStateToProps,
    mapDispatchToProps
  )(BaseProcessesDashboard)
);

export const ProcessesDashboardBreadcrumb = (props: any) => (
  <BreadcrumbItemStructure
    {...props}
    icon={processCt.icon}
    text={gt('Processes')}
    link={PROCESS_DASHBOARD_URL}
  />
);
